/**
 * Classe d'appareil : la nature du terminal qui visite Virtuosa, réduite à
 * trois valeurs fermées.
 *
 * **Pourquoi dans le contexte de lot et pas dans `page_viewed`.** C'est une
 * propriété de la *session*, de la même nature que le palier de performance :
 * elle ne change pas d'un événement à l'autre. Portée par le contexte, elle
 * segmente tous les événements ; portée par la page vue, elle n'en
 * segmenterait qu'un — et l'entonnoir « arrivé sur mobile → jamais entré au
 * studio » resterait illisible, alors que c'est précisément la question posée
 * par une cohorte recrutée sur les réseaux sociaux.
 *
 * **Pourquoi trois valeurs et pas un modèle d'appareil.** Le produit n'a
 * qu'une question, et elle est grossière : le téléphone est une surface de
 * découverte (on y arrive depuis un lien, on n'y joue pas), la tablette est le
 * support idéal (posée sur le pupitre du piano), l'ordinateur est le poste de
 * travail. Trois classes suffisent à mesurer ce qui se perd entre la
 * découverte et la pratique, et à voir si l'on revient plus tard sur grand
 * écran. Toute finesse supplémentaire ne servirait rien et coûterait de
 * l'identifiabilité.
 *
 * **Pourquoi ces signaux et pas la chaîne d'agent utilisateur.** La posture du
 * produit interdit l'empreinte : rien n'est écrit sur le terminal, aucune IP
 * ne part chez un tiers, et l'agent utilisateur — qui identifie à lui seul un
 * appareil à quelques bits près — ne quitte donc jamais le navigateur. Les
 * deux signaux retenus ici ne disent rien de plus que ce que le contexte de
 * lot transporte déjà : un booléen de type de pointeur, et des dimensions de
 * viewport déjà collectées.
 */

/**
 * Les trois classes. `phone` plutôt que `mobile` parce qu'une tablette est
 * mobile elle aussi : la distinction utile n'est pas la mobilité, c'est la
 * surface disponible pour un clavier de piano.
 */
export type DeviceClass = "phone" | "tablet" | "desktop";

/**
 * Seuil de bascule téléphone/tablette, en pixels CSS, appliqué au **petit
 * côté** du viewport pour rester insensible à l'orientation — un téléphone en
 * paysage est toujours un téléphone.
 *
 * 600 est la frontière `sw600dp` d'Android, éprouvée depuis quinze ans et
 * confortablement au milieu du fossé réel : les téléphones les plus larges
 * plafonnent vers 430 px de petit côté, l'iPad mini en donne 744 et un pliable
 * déplié environ 670. Aucun appareil courant ne se tient sur la ligne.
 */
const TABLET_MIN_SHORT_SIDE_PX = 600;

/** Les seuls signaux dont dépend la classification. */
export interface DeviceSignals {
  /**
   * `(pointer: coarse)` : le pointeur **primaire** est un doigt. Un portable
   * tactile répond `false` — son pointeur primaire reste le trackpad — ce qui
   * est le comportement voulu : c'est bien un ordinateur.
   */
  coarsePointer: boolean;

  /** Petit côté du viewport, en pixels CSS. */
  shortSidePx: number;
}

/**
 * Fonction pure, séparée de la lecture du navigateur pour rester vérifiable
 * sans simuler un DOM entier : c'est la règle qui mérite un test, pas l'appel
 * à `matchMedia`.
 */
export function classifyDevice({ coarsePointer, shortSidePx }: DeviceSignals): DeviceClass {
  // Un pointeur fin (souris, trackpad, stylet) tranche à lui seul : aucun
  // téléphone n'en a un en primaire, et la taille du viewport ne dit rien
  // d'utile sur un ordinateur, où c'est la fenêtre qu'on redimensionne.
  if (!coarsePointer) {
    return "desktop";
  }

  return shortSidePx >= TABLET_MIN_SHORT_SIDE_PX ? "tablet" : "phone";
}

/**
 * Classe de l'appareil courant, ou `null` hors navigateur (rendu serveur,
 * tests) : l'inconnu se déclare, il ne se devine pas.
 */
export function detectDeviceClass(): DeviceClass | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  return classifyDevice({
    coarsePointer: window.matchMedia("(pointer: coarse)").matches,
    shortSidePx: Math.round(Math.min(window.innerWidth, window.innerHeight)),
  });
}
